import { Popover } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import { TooltipPlacement } from 'antd/lib/tooltip';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId, Team, User } from 'api/models';
import { Button } from 'components/atoms/button';
import {
  CustomDueDatePicker,
  DateValue,
} from 'components/atoms/due-date-picker';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { MentionData, OnUserMention } from 'components/molecules/mention';
import { TaskAssigneePicker } from 'components/molecules/squad-picker/assignee-picker';
import { Option } from 'components/molecules/squad-picker/types';
import { SquadData } from 'components/molecules/squad-picker/use-squad-options';
import { squadToMembersResponse } from 'components/molecules/squad-picker/utils';
import { Task } from 'data/tasks';
import { useTranslation } from 'hooks/use-translation';
import { isRelatedEl } from './is-related-element';
import './new-task-popup.css';
import {
  NegativeMarginWrapper,
  PopupContent,
  SquadPicker,
  TaskActions,
  TaskTitle,
} from './new-task-popup.style';

interface NewTaskPopupProps {
  attachTo?: HTMLElement | null;
  placement?: TooltipPlacement;
  assignee?: {
    options?: SquadData;
    isLoading: boolean;
  };
  projectId?: number;
  isPrivate?: boolean;
  // Simple assignee picker is used by templates without any other logic
  showSimpleAssigneePicker?: boolean;
  mentionData: MentionData;
  onUserMention?: OnUserMention;
  canAddDate?: boolean;
  visible: boolean;
  onClose: () => void;
  onSubmit(task: Partial<Task>): void;
  isLoading?: boolean;
}

export const NewTaskPopup: FC<NewTaskPopupProps> = ({
  attachTo,
  children,
  placement,
  assignee: assigneeFromProp,
  projectId,
  isPrivate,
  showSimpleAssigneePicker,
  mentionData,
  onUserMention,
  canAddDate = true,
  visible,
  onClose,
  isLoading = false,
  onSubmit,
}) => {
  const [title, setTitle] = useState('');
  const inputRef = useRef<TextAreaRef>(null);
  const [assignee, setAssignee] = useState<Option[]>([]);

  const [dueDate, setDueDate] = useState<DateValue>();
  const [startDate, setStartDate] = useState<DateValue>();
  const [description, setDescription] = useState('');

  const taskDescLabel = useTranslation('task.description.placeholder');
  const taskNameLabel = useTranslation('task_name');

  const hasFormData = () =>
    title ||
    assignee.length > 0 ||
    dueDate ||
    startDate ||
    description.length > 0;

  const cleanupForm = () => {
    setTitle('');
    setAssignee([]);
    setDueDate(undefined);
    setStartDate(undefined);
    setDescription('');
  };
  const closeForm = () => {
    cleanupForm();
    onClose();
  };

  const handleAssigneeChange = (/* selectedAssignee */) => {
    // TODO: For templates, we may need to show responsible fields instead of
    // assignees
  };

  const handleDueDateChange = ([_startDate, _dueDate]) => {
    setDueDate(_dueDate);
    setStartDate(_startDate);
  };

  useEffect(() => {
    window.setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const onFormSubmitTask = () => {
    const userPayload = (o: Option) =>
      ({ ...o, userId: o.extra.id } as unknown);
    const teamPayload = (o: Option) =>
      ({ ...o, teamId: o.extra.id } as unknown);
    const users = assignee
      .filter((o) => o.extra.type === 'user')
      .map(userPayload);

    const teams = assignee
      .filter((o) => o.extra.type === 'team')
      .map(teamPayload);

    const newTask = {
      dueDate,
      startDate,
      taskName: title,
      taskDescription: description,
      assignee: {
        users: users as User[],
        teams: teams as Team[],
      },
    };

    onSubmit(newTask);
    cleanupForm();
  };

  return (
    <Popover
      getPopupContainer={attachTo ? () => attachTo : undefined}
      placement={placement}
      overlayClassName="rl-new-task-popup"
      visible={visible}
      destroyTooltipOnHide
      content={
        <PopupContent>
          <OutsideClickHandler
            onOutsideClick={() => {
              if (!hasFormData()) {
                closeForm();
              }
            }}
            allowed={isRelatedEl}
          >
            <TaskTitle
              autoFocus
              ref={inputRef}
              placeholder={taskNameLabel}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={(e) => {
                setTitle(e.target.value);
              }}
              autoSize={{ minRows: 1, maxRows: 6 }}
            />

            {showSimpleAssigneePicker ? (
              <SquadPicker
                options={
                  assigneeFromProp?.options &&
                  squadToMembersResponse(assigneeFromProp?.options)
                }
                isLoading={assigneeFromProp?.isLoading}
                onChange={handleAssigneeChange}
              />
            ) : (
              projectId && (
                <NegativeMarginWrapper>
                  <TaskAssigneePicker
                    isPrivate={isPrivate}
                    projectId={ProjectId(projectId)}
                    value={assignee}
                    onChange={setAssignee}
                    startDate={startDate}
                    dueDate={dueDate}
                  />
                </NegativeMarginWrapper>
              )
            )}
            {canAddDate && (
              <NegativeMarginWrapper>
                <CustomDueDatePicker
                  block
                  startDate={startDate}
                  dueDate={dueDate}
                  onDateChange={handleDueDateChange}
                />
              </NegativeMarginWrapper>
            )}
            <RichTextEditor
              onChange={(data) => {
                setDescription(data);
              }}
              data={description}
              onEnter={onFormSubmitTask}
              placeholder={taskDescLabel}
              mentionData={mentionData}
              onUserMention={onUserMention}
            />
            <TaskActions>
              <Button
                size="small"
                data-testid="submit"
                onClick={onFormSubmitTask}
                disabled={!title.length}
                type="primary"
                loading={isLoading}
              >
                <FormattedMessage id="create" />
              </Button>
              <Button size="small" onClick={closeForm} data-testid="cancel">
                <FormattedMessage id="cancel" />
              </Button>
            </TaskActions>
          </OutsideClickHandler>
        </PopupContent>
      }
    >
      {children}
    </Popover>
  );
};
